import React, { useState } from "react";
import { FaExchangeAlt, FaFolderOpen, FaFileInvoiceDollar } from "react-icons/fa";
import { SwalAboveAppModal } from "../../utilis/swalAboveAppModal";
import {
  getItemRecordId,
  recategorizeClientDocument,
  recategorizeClientNote,
} from "../../utilis/clientRecategorize";

const CATEGORY_LABELS = {
  general: "General",
  financial: "Financial",
  finance: "Financial",
};

const isFinanceCategory = (category) =>
  category === "finance" || category === "financial";

const RecategorizeCategoryControl = ({
  itemType,
  item,
  clientId,
  currentCategory,
  onSuccess,
  className = "",
  variant = "default",
  appId,
  appType,
}) => {
  const [loading, setLoading] = useState(false);
  const targetCategory =
    itemType === "document"
      ? isFinanceCategory(currentCategory)
        ? "general"
        : "finance"
      : currentCategory === "financial"
        ? "general"
        : "financial";
  const itemId = getItemRecordId(item);

  if (!itemId || !clientId) return null;

  const handleRecategorize = async () => {
    const itemLabel = itemType === "document" ? "document" : "note";
    const fromLabel = CATEGORY_LABELS[currentCategory] || currentCategory;
    const toLabel = CATEGORY_LABELS[targetCategory] || targetCategory;

    const confirmed = await SwalAboveAppModal.fire({
      title: "Change category?",
      text: `Move this ${itemLabel} from ${fromLabel} to ${toLabel}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Move to ${toLabel}`,
      cancelButtonText: "Cancel",
    });

    if (!confirmed.isConfirmed) return;

    setLoading(true);
    try {
      if (itemType === "document") {
        await recategorizeClientDocument({
          clientId,
          documentId: itemId,
          category: targetCategory,
          appId,
          appType,
        });
      } else {
        await recategorizeClientNote({
          clientId,
          noteId: itemId,
          note: item,
          currentCategory,
          category: targetCategory,
        });
      }

      if (onSuccess) {
        await Promise.resolve(onSuccess(targetCategory));
      }

      await SwalAboveAppModal.fire({
        title: "Updated",
        text: `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} moved to ${toLabel}.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      SwalAboveAppModal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          error.message ||
          "Could not update category.",
      });
    } finally {
      setLoading(false);
    }
  };

  const targetLabel = CATEGORY_LABELS[targetCategory] || targetCategory;
  const actionLabel = `Move to ${targetLabel}`;
  const moveHint =
    targetCategory === "finance" || targetCategory === "financial"
      ? "Move this document to Financial documents"
      : "Move this document to General documents";

  const isModalVariant = variant === "modal";
  const isIconVariant = variant === "icon";
  const isSelectedTone = className.includes("is-selected-tone");

  if (isIconVariant) {
    const TargetIcon =
      targetCategory === "finance" || targetCategory === "financial"
        ? FaFileInvoiceDollar
        : FaFolderOpen;
    const iconBtnClass = isSelectedTone
      ? "border-white/25 bg-white/10 text-white hover:bg-white/20"
      : "border-slate-200/80 bg-white text-slate-500 hover:border-[#007BFF]/35 hover:text-[#007BFF]";

    return (
      <button
        type="button"
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition disabled:opacity-50 ${iconBtnClass} ${className}`.trim()}
        style={{ boxShadow: "none" }}
        onClick={(event) => {
          event.stopPropagation();
          handleRecategorize();
        }}
        disabled={loading}
        title={loading ? "Moving…" : moveHint}
        aria-label={actionLabel}
      >
        {loading ? (
          <FaExchangeAlt size={11} className="animate-pulse" />
        ) : (
          <TargetIcon size={11} />
        )}
      </button>
    );
  }

  const buttonClass = isModalVariant
    ? `inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-[#007BFF]/30 hover:text-[#007BFF] disabled:opacity-60 ${className}`.trim()
    : `btn btn-outline-secondary btn-sm py-0 px-2 ${className}`.trim();

  const buttonStyle = isModalVariant
    ? { boxShadow: "none", whiteSpace: "nowrap" }
    : {
        fontSize: "11px",
        lineHeight: 1.3,
        marginTop: "4px",
        whiteSpace: "nowrap",
      };

  return (
    <button
      type="button"
      className={buttonClass}
      style={buttonStyle}
      onClick={handleRecategorize}
      disabled={loading}
      title={actionLabel}
    >
      {loading ? "Moving..." : actionLabel}
    </button>
  );
};

export default RecategorizeCategoryControl;
